import { DateTime } from 'luxon';
import { FC } from 'react';
import styled, { css } from 'styled-components';

import Text from '@/components/Text';
import { DQFeedRecord } from '@/types/resources';

import AutoPlayVideo from './AutoPlayVideo';

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
  overflow: hidden;
  max-width: 600px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
`;

const ChannelThumb = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 10px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
`;

const Message = styled(Text)`
  padding: 0 16px 12px;
  font-size: 15px;
  line-height: 1.4;
`;

const Media = styled.div`
  width: 100%;
  background: #000;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  img,
  video {
    max-width: 100%;
    max-height: 70vh;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 16px;
  border-top: 1px solid #f0f0f0;
  font-size: 13px;
`;

const Stat = styled(Text)<{ $link?: boolean }>`
  margin-right: 12px;

  ${({ $link = false, theme }) =>
    $link &&
    css`
      cursor: pointer;
      &:hover {
        color: ${theme.colors.purple};
      }
    `}
`;

interface FeedCardProps {
  post: DQFeedRecord;
}

const FeedCard: FC<FeedCardProps> = ({ post }) => {
  return (
    <Card>
      <Header>
        {post.channel.thumb && (
          <ChannelThumb src={post.channel.thumb} alt={post.channel.name} />
        )}
        <Text $type="caption">{post.channel.name}</Text>
      </Header>
      <Body>
        {post.message && <Message>{post.message}</Message>}
        <Media>
          {post.video ? (
            <AutoPlayVideo src={post.video} />
          ) : post.thumb ? (
            <img src={post.thumb} alt="Post media" loading="lazy" />
          ) : null}
        </Media>
      </Body>
      <Footer>
        <div>
          <Stat as="span">👍 {post.stats?.[0]?.reactions ?? 0}</Stat>
          <Stat as="span">💬 {post.stats?.[0]?.comments ?? 0}</Stat>
          <Stat as="span">↗️ {post.stats?.[0]?.shares ?? 0}</Stat>
          {!!post.permalink && (
            <Stat
              as="a"
              $link
              href={post.permalink}
              target="_blank"
              rel="noreferrer noopener"
            >
              Post ↗
            </Stat>
          )}
          {!!post.link && (
            <Stat
              as="a"
              $link
              href={post.link}
              target="_blank"
              rel="noreferrer noopener"
            >
              Article ↗
            </Stat>
          )}
        </div>
        <Text as="span">{DateTime.fromISO(post.publishedAt).toRelative()}</Text>
      </Footer>
    </Card>
  );
};

export default FeedCard;
